import type { IItemTypeAgentRules, IWorkAreaRuleSources } from '@featit/shared';
import { expectRecord } from './answerShape';
import { workAreaGql } from '@/data-sources/WorkAreaApiRegistry';

/**
 * The policy the server will actually evaluate for a work area — **not** the blob the work area
 * stores.
 *
 * ⛔ The difference is the whole of `I-0541`. A work area on `policySource: 'global'` — which is
 * every work area that has never been given a template — stores no `workItems.default` at all, so a
 * screen reading its own policies resolves every terminal gate to `false` and states that closing
 * needs nothing, while the server refuses the close on the global default it inherits. The UI was
 * not merely silent about the rule; it asserted its negation, and left Submit enabled.
 *
 * `workAreaEffectiveRules` is the server's own answer, from `WorkItemsService.effectiveWorkAreaRules`
 * — the same inheritance decision every write enforces (`I-0341`). Reading it is what makes the
 * dialog state the rules that are about to be applied rather than a second opinion about them.
 */
export interface IWorkAreaEffectiveRules {
  workAreaId: string;
  /** The normalized default policy, global default composed in. */
  defaultPolicy: Record<string, unknown>;
  policySource: 'global' | 'project';
  /**
   * The terminal gates each item type's moves are checked against, keyed by item type key, as the
   * server enforces them (`I-0541`). The web states these as they are and merges no layers of its
   * own: its merge was a copy of `effectivePolicyFor` that had already missed `I-0579`'s profile
   * seal and the `enabled` switch. Keys are the server's; one this build does not know is kept and
   * stated rather than dropped.
   */
  terminalGates?: Record<string, Record<string, boolean>>;
  /**
   * Every rule in force and the layer it comes from (I-0795): what Settings → Policy states. Absent
   * only from a server older than this field.
   */
  ruleSources?: IWorkAreaRuleSources;
  /**
   * Each item type's agent rules and where they come from (F-0274 GV-1a/1b): the flow the work area
   * published for it (`authored`), or the flow generated from its policies. Settings → Workflow
   * reads it to say that an authored flow's type takes its moves and agent rules from that flow
   * (GV-1b.5). Absent only from a server older than this field.
   */
  agentRules?: IItemTypeAgentRules[];
}

const WORK_AREA_EFFECTIVE_RULES_QUERY = `query WorkAreaEffectiveRules($workAreaId: ID!) {
  workAreaEffectiveRules(workAreaId: $workAreaId) {
    workAreaId
    defaultPolicy
    policySource
    terminalGates
    ruleSources
    agentRules
  }
}`;

export function fetchWorkAreaEffectiveRules(workAreaId: string): Promise<IWorkAreaEffectiveRules> {
  return workAreaGql(workAreaId)<{ workAreaEffectiveRules: IWorkAreaEffectiveRules }>(
    WORK_AREA_EFFECTIVE_RULES_QUERY,
    { workAreaId },
  ).then((data) =>
    expectRecord<IWorkAreaEffectiveRules>(data.workAreaEffectiveRules, 'The rules in force', [
      'defaultPolicy',
    ]),
  );
}

/**
 * One person's or agent's count against the work-in-progress cap, as the server counts it for the
 * next move into In progress (F-0277, GV-7) — `limit` is theirs, an agent's live override included.
 */
export interface IWorkAreaInProgressLoadEntry {
  actorType: 'user' | 'agent' | string;
  actorId: string;
  label: string;
  count: number;
  limit: number;
}

export interface IWorkAreaInProgressLoad {
  workAreaId: string;
  /** Null when the work area sets no cap (or an unreadable one): then there is nothing to count. */
  maxInProgress: number | null;
  entries: IWorkAreaInProgressLoadEntry[];
}

const WORK_AREA_IN_PROGRESS_LOAD_QUERY = `query WorkAreaInProgressLoad($workAreaId: ID!) {
  workAreaInProgressLoad(workAreaId: $workAreaId) {
    workAreaId
    maxInProgress
    entries { actorType actorId label count limit }
  }
}`;

export function fetchWorkAreaInProgressLoad(workAreaId: string): Promise<IWorkAreaInProgressLoad> {
  return workAreaGql(workAreaId)<{ workAreaInProgressLoad: IWorkAreaInProgressLoad }>(
    WORK_AREA_IN_PROGRESS_LOAD_QUERY,
    { workAreaId },
  ).then((data) => data.workAreaInProgressLoad);
}
